import { LoadingButton } from '@mui/lab';
import {
  Box,
  Stack,
  Dialog,
  Button,
  Divider,
  MenuItem,
  Typography,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useForm, useFieldArray, FieldArrayWithId } from 'react-hook-form';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';
import { PositionTxType } from 'src/type/position';
import { TransactionType } from 'src/type/transaction';
import axios, { endpoints } from 'src/utils/axios';
import { toastError } from 'src/utils/format-error';
import useProductFeeOptions from 'src/hooks/use-product-fee-options';
import Iconify from 'src/components/iconify';
import { fNumber } from 'src/utils/format-number';
import { useState, useEffect } from 'react';
import { BranchType } from 'src/type/branch';
import { useBoolean } from 'src/hooks/use-boolean';
import { ProductRedeemFeeType } from 'src/type/product';
import TransactionSelectDialog, {
  TransactionCard,
} from 'src/components/ui-kit/transaction-select-dialog';
import { convertGold } from 'src/utils/convert';
import useCurrentDateTime from 'src/hooks/use-current-date-time';
import { txAction } from './utils';

const approveSellOptions = [
  // {
  //   value: 'APPROVE_SELL_POSITION_1',
  //   label: 'รับทองจริง',
  // },
  {
    value: 'APPROVE_SELL_POSITION_2',
    label: 'ตัดตั๋วทอง',
  },
];

type ApproveDialogProps = {
  open: boolean;
  onClose: () => void;
  tx: PositionTxType;
  handleReload: () => void;
  branches: BranchType[];
};

export default function SellApproveDialog({
  open,
  onClose,
  tx,
  handleReload,
  branches,
}: ApproveDialogProps) {
  const currentDateTime = useCurrentDateTime();
  // console.log({ tx });
  const select = useBoolean(false);
  const branchError = useBoolean(false);

  const { productFeeOptions } = useProductFeeOptions(tx.productId, tx?.unitName);

  const [txFilters, setTxFilters] = useState<PositionTxType[]>(tx.refTxs?.length ? tx.refTxs : []);
  const [totalWeight2, setTotalWeight2] = useState(0);

  const defaultValues = {
    note: '',
    type: 'APPROVE_SELL_POSITION_2',
    fee: [{ id: '', amount: 0 }],
    branchId: branches[0]?.id || '',
  };

  const methods = useForm({
    mode: 'onChange',
    defaultValues,
  });

  const {
    handleSubmit,
    watch,
    reset,
    control,
    formState: { isSubmitting },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'fee',
  });

  const feeWithInfo = watch().fee?.map((fee) => {
    const redeemFee = productFeeOptions.find((option) => option.value === fee.id)?.info;
    const isBaht = tx.unitName === 'บาท';
    const weight = isBaht ? redeemFee?.weightBaht || 0 : redeemFee?.weightGram || 0;
    return {
      ...fee,
      weight,
    };
  });

  const totalWeight1 = feeWithInfo?.reduce((sum, item) => sum + item.amount * item.weight, 0) || 0;
  // console.log(watch().fee);
  // console.log({ feeWithInfo });
  useEffect(() => {
    setTotalWeight2(0);
    if (watch().type === 'APPROVE_SELL_POSITION_2') {
      reset({
        ...watch(),
        fee: [{ id: '', amount: 0 }],
      });
      if (tx.refTxs?.length) {
        setTxFilters(tx.refTxs);
        const total = tx.refTxs.reduce((sum, item) => sum + item.remainGoldAmount, 0) || 0;
        setTotalWeight2(total);
      } else {
        setTxFilters([]);
      }
    }
    if (watch().type === 'APPROVE_SELL_POSITION_1') {
      setTxFilters([]);
    }
  }, [watch().type, tx.refTxs]);

  useEffect(() => {
    if (watch().branchId) {
      branchError.onFalse();
    }
  }, [watch().branchId]);

  const beforeClose = () => {
    reset(defaultValues);
    onClose();
  };

  const handleAdd = () => {
    append({
      id: '',
      amount: 0,
    });
  };

  const handleRemove = (index: number) => {
    remove(index);
  };

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (!values.branchId) {
        branchError.onTrue();
        return;
      }

      if (values.type === 'APPROVE_SELL_POSITION_1') {
        if (!values?.fee?.length) {
          throw new Error('กรุณาเพิ่มค่าธรรมเนียม');
        }
        if (totalWeight1 !== tx.amount) {
          throw new Error('จำนวนทองตัดขายไม่ตรงกับจำนวนทองที่ต้องการตัด');
        }
        await axios.post(endpoints.transactions.position.sellApprove1(tx.id), {
          note: values.note,
          productRedeemFees: values.fee,
          branchId: values.branchId,
          currentDateTime,
        });
      } else {
        if (txFilters.length === 0) {
          throw new Error('กรุณาเลือกรายการตั๋วทอง');
        }
        await axios.post(endpoints.transactions.position.sellApprove2(tx.id), {
          note: values.note,
          branchId: values.branchId,
          refTxIds: txFilters.map((item) => item.id).join(','),
          currentDateTime,
        });
      }

      branchError.onFalse();
      handleReload();
      beforeClose();
      enqueueSnackbar('อัพเดทข้อมูลสำเร็จ', { variant: 'success' });
    } catch (error) {
      toastError(error);
    }
  });

  const branchOption =
    branches && branches.length > 0
      ? branches.map((branch) => ({
          label: branch.name,
          value: branch.id,
        }))
      : [];

  // const handleSelectTxs = (items: PositionTxType[]) => {
  //   setTxFilters(items);
  //   select.onFalse();
  //   const total = items.reduce((sum, item) => sum + item.remainGoldAmount, 0) || 0;
  //   setTotalWeight2(total);
  // };

  const handleSelectTxs = (items: PositionTxType[]) => {
    const isTxGram = tx.unitName === 'กรัม';

    setTxFilters(items);
    select.onFalse();
    const total = items.reduce((acc, item) => {
      let remainGold = item.remainGoldAmount;

      switch (true) {
        case isTxGram && item.unitName === 'กรัม':
        case !isTxGram && item.unitName === 'บาท':
          break;
        case isTxGram && item.unitName === 'บาท':
          remainGold = convertGold(item.remainGoldAmount, 'weightGrams');
          break;
        case !isTxGram && item.unitName === 'กรัม':
          remainGold = convertGold(item.remainGoldAmount, 'weightBaht');
          break;
        default:
          remainGold = 0;
          break;
      }

      return acc + remainGold;
    }, 0);
    setTotalWeight2(total);
  };

  // useEffect(() => {
  //   const total = txFilters.reduce((sum, item) => sum + item.remainGoldAmount, 0) || 0;
  //   setTotalWeight(total);
  // }, [txFilters]);

  const isSellFromWalletPage = !!tx.refTxs?.length;

  return (
    <>
      <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
        <FormProvider methods={methods} onSubmit={onSubmit}>
          <Stack direction="row" alignItems="center" sx={{ p: 3 }} flexWrap="wrap">
            <Typography variant="h6">อนุมัติการ</Typography>
            <Typography
              color={tx.type === TransactionType.BUY_POSITION ? 'success.main' : 'error'}
              variant="h6"
              mx={0.5}
            >
              {txAction(tx.type)}
            </Typography>
            <Typography variant="h6">
              {tx.product.name} {tx.amount} {tx.unitName}
            </Typography>
          </Stack>
          <DialogContent>
            <Stack sx={{ py: 1 }} spacing={2}>
              {tx.type === TransactionType.SELL_POSITION && (
                <Stack spacing={3}>
                  <RHFSelect
                    name="type"
                    label="ประเภทการตัดขาย"
                    InputLabelProps={{ shrink: true }}
                    placeholder="ประเภทการตัดขาย"
                    disabled={!!tx.refTxs?.length}
                  >
                    {approveSellOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </RHFSelect>

                  {watch('type') === 'APPROVE_SELL_POSITION_1' && (
                    <ApproveSellPosition1
                      fields={fields}
                      handleAdd={handleAdd}
                      handleRemove={handleRemove}
                      tx={tx}
                      totalWeight={totalWeight1}
                      productFeeOptions={productFeeOptions}
                    />
                  )}

                  {watch('type') === 'APPROVE_SELL_POSITION_2' && (
                    <ApproveSellPosition2
                      tx={tx}
                      select={select}
                      txFilters={txFilters}
                      totalWeight={totalWeight2}
                      txWeight={tx.amount}
                      isSellFromWalletPage={isSellFromWalletPage}
                    />
                  )}
                </Stack>
              )}

              <Divider sx={{ borderStyle: 'dashed' }} />

              <RHFSelect
                name="branchId"
                label="สาขาที่ดำเนินการ"
                placeholder="เลือกสาขาที่ดำเนินการ"
                error={branchError.value}
                helperText={branchError.value ? 'กรุณาเลือกสาขาที่ดำเนินการ' : ''}
              >
                {branchOption.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </RHFSelect>

              <RHFTextField
                multiline
                rows={3}
                name="note"
                placeholder="หมายเหตุ"
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={beforeClose} disabled={isSubmitting}>
              ยกเลิก
            </Button>
            <LoadingButton
              variant="contained"
              type="submit"
              loading={isSubmitting}
              disabled={
                (totalWeight1 !== tx.amount && watch('type') === 'APPROVE_SELL_POSITION_1') ||
                (isSellFromWalletPage
                  ? totalWeight2 > totalWeight2 + tx.amount &&
                    watch('type') === 'APPROVE_SELL_POSITION_2'
                  : totalWeight2 < tx.amount && watch('type') === 'APPROVE_SELL_POSITION_2')
              }
            >
              ดำเนินการต่อ
            </LoadingButton>
          </DialogActions>
        </FormProvider>
      </Dialog>

      <TransactionSelectDialog
        open={select.value}
        onClose={select.onFalse}
        selectedItems={txFilters}
        onSelects={handleSelectTxs}
        user={tx.user}
        productId={tx.productId}
        productName={tx.product.name}
        unitName={tx.unitName}
      />
    </>
  );
}

function ApproveSellPosition1({
  fields,
  handleAdd,
  handleRemove,
  tx,
  totalWeight,
  productFeeOptions,
}: {
  fields: FieldArrayWithId[];
  handleAdd: VoidFunction;
  handleRemove: (index: number) => void;
  tx: PositionTxType;
  totalWeight: number;
  productFeeOptions: {
    label: string;
    value: string;
    info: ProductRedeemFeeType;
  }[];
}) {
  return (
    <>
      <Stack divider={<Divider flexItem sx={{ borderStyle: 'dashed' }} />} spacing={2}>
        {fields.map((item, index) => (
          <Stack key={item.id} alignItems="flex-end" spacing={1.5}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ width: 1 }}>
              <RHFSelect
                size="small"
                name={`fee[${index}].id`}
                label="ทอง"
                InputLabelProps={{ shrink: true }}
                placeholder="ทอง"
              >
                {productFeeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </RHFSelect>
              <RHFTextField
                size="small"
                name={`fee[${index}].amount`}
                type="number"
                label="จำนวน"
                placeholder="0"
                InputLabelProps={{ shrink: true }}
              />
              {fields.length > 1 && (
                <Button
                  size="small"
                  color="error"
                  startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
                  onClick={() => handleRemove(index)}
                >
                  ลบ
                </Button>
              )}
            </Stack>
          </Stack>
        ))}
      </Stack>

      <Stack spacing={3} direction="row" justifyContent="space-between">
        <Button
          size="small"
          color="primary"
          startIcon={<Iconify icon="mingcute:add-line" />}
          onClick={handleAdd}
          sx={{ flexShrink: 0 }}
        >
          เพิ่ม
        </Button>

        <Stack alignItems="flex-end" sx={{ width: 1 }}>
          <Typography
            variant={totalWeight !== tx.amount ? 'subtitle1' : 'subtitle2'}
            color={totalWeight !== tx.amount ? 'error' : 'success.main'}
          >
            จำนวนทองรวม {fNumber(totalWeight)} {tx.unitName}
          </Typography>
          <Typography variant="subtitle2">
            จำนวนทองตัดขาย {fNumber(tx.amount)} {tx.unitName}
          </Typography>
        </Stack>
      </Stack>
    </>
  );
}

function ApproveSellPosition2({
  tx,
  select,
  txFilters,
  totalWeight,
  txWeight,
  isSellFromWalletPage,
}: {
  tx: PositionTxType;
  select: ReturnType<typeof useBoolean>;
  txFilters: PositionTxType[];
  totalWeight: number;
  txWeight?: number;
  isSellFromWalletPage?: boolean;
}) {
  return (
    <Stack spacing={1}>
      {!tx.refTxIds && (
        <Box
          onClick={select.onTrue}
          sx={{
            width: 1,
            height: 54,
            display: 'flex',
            alignItems: 'center',
            border: (theme_) => `1px solid ${theme_.palette.divider}`,
            borderRadius: 1,
            px: 1.5,
            cursor: 'pointer',
          }}
        >
          <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
          <Typography variant="body2" sx={{ color: 'text.disabled', ml: 1 }}>
            เลือกรายการตั๋วทอง
          </Typography>
        </Box>
      )}

      {txFilters.length ? (
        <Stack spacing={1}>
          {txFilters.map((item) => (
            <TransactionCard
              key={item.id}
              tx={item}
              onClick={() => {}}
              disableClick
              txWeight={txWeight}
              isSellFromWalletPage={isSellFromWalletPage}
            />
          ))}
        </Stack>
      ) : null}

      {!tx.refTxIds && (
        <Stack alignItems="flex-end" sx={{ width: 1 }}>
          <Typography
            variant={totalWeight < tx.amount ? 'subtitle1' : 'subtitle2'}
            color={totalWeight < tx.amount ? 'error' : 'success.main'}
          >
            จำนวนทองก่อนตัดขาย รวม {fNumber(tx.refTxIds ? totalWeight + tx.amount : totalWeight)}{' '}
            {tx.unitName}
          </Typography>
          <Typography variant="subtitle2">
            {txFilters.length === 0 ? 'จำนวนทองที่ต้องตัดขาย' : 'จำนวนทองที่ตัดขาย'}{' '}
            {fNumber(tx.amount)} {tx.unitName}
          </Typography>
          {totalWeight - tx.amount >= 0 && (
            <Typography variant="subtitle2">
              จำนวนทองคงเหลือ {fNumber(totalWeight - tx.amount)} {tx.unitName}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
}
